import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import Link from 'components/UI/Link';
import RndVideosCards from './RndVideosCards';
import SubmenuLink from './SubmenuLink';
import ImageLink from './ImageLink';
import { useHistoryCallback } from 'utils/routing';
import { pushHeaderEvent, handleLinkClick } from '../../../utils';

import * as styles from './TabsWithLinks.module.scss';

export default function TabsWithLinks({ links, subMenuActive }) {
  const [selected, setSelected] = useState(links[0].title);
  const [fadeInAnimation, setFadeInAnimation] = useState(false);
  const tabRefs = useRef({});

  const setDesktopSelectedTab = payload => {
    setSelected(payload);
    pushHeaderEvent('click_on_tab', payload, 'Desktop');
    setFadeInAnimation(true);
  };

  const onAnimationEnd = () => {
    setFadeInAnimation(false);
  };

  const handleKeyDown = (e, currentTitle) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const tabTitles = links.map(link => link.title);
      const currentIndex = tabTitles.indexOf(currentTitle);

      let newIndex;
      if (e.key === 'ArrowLeft') {
        newIndex = currentIndex > 0 ? currentIndex - 1 : tabTitles.length - 1;
      } else {
        newIndex = currentIndex < tabTitles.length - 1 ? currentIndex + 1 : 0;
      }

      const newTitle = tabTitles[newIndex];
      setDesktopSelectedTab(newTitle);
      tabRefs.current[newTitle]?.focus();
    }
  };

  useHistoryCallback(() => {
    setSelected(links[0].title);
  });

  useEffect(() => {
    if (!subMenuActive) {
      setSelected(links[0].title);
    }
  }, [subMenuActive]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.tabs} role="tablist" aria-label="Menu navigation">
        {links.map(({ title }, i) => {
          const isCurrentTab = title === selected;

          return (
            <button
              key={`${title}-${i}`}
              ref={el => {
                tabRefs.current[title] = el;
              }}
              id={`${title}-tab-${i}`}
              className={cn(styles.tabButton, isCurrentTab && styles.active)}
              role="tab"
              aria-selected={isCurrentTab}
              aria-controls={`${title}-panel-${i}`}
              tabIndex={subMenuActive && isCurrentTab ? 0 : -1}
              onClick={() => setDesktopSelectedTab(title)}
              onKeyDown={e => handleKeyDown(e, title)}
            >
              <p className={styles.tabTitle}>{title}</p>
            </button>
          );
        })}
      </div>

      <div
        className={cn(styles.content, fadeInAnimation && styles.fadeInAnimation)}
        onAnimationEnd={onAnimationEnd}
      >
        {links.map(({ title, linkList, contentType, parentLink }, i) => {
          const isCurrentTab = title === selected;

          return (
            <div
              key={`${title}-${i}`}
              id={`${title}-panel-${i}`}
              className={cn(styles.tabPanel, !isCurrentTab && styles.hidden)}
              role="tabpanel"
              aria-labelledby={`${title}-tab-${i}`}
              tabIndex={subMenuActive && isCurrentTab ? 0 : -1}
            >
              {renderLinks(contentType, linkList, subMenuActive)}
              {parentLink && (
                <Link
                  href={parentLink.url}
                  className={styles.parentLink}
                  tabIndex={subMenuActive ? 0 : -1}
                  onClick={() => handleLinkClick(parentLink.url)}
                >
                  {parentLink.title}
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function renderLinks(contentType, linkList, subMenuActive) {
  if (contentType === 'rnd-videos') {
    return <RndVideosCards />;
  }

  const withImages = linkList?.some(item => Boolean(item.img));
  return (
    <div className={cn(styles.linksWrap, withImages && styles.imagesWrap)}>
      {linkList?.map((link, i) => {
        if (link.img) {
          return (
            <ImageLink
              key={`image-${i}`}
              {...link}
              dir="rtl"
              onClick={() => pushHeaderEvent('click_on_case_card', link.title, 'Desktop')}
              tabIndex={subMenuActive ? 0 : -1}
            />
          );
        }
        return (
          <SubmenuLink
            key={`link-${i}`}
            {...link}
            className={styles.link}
            tabIndex={subMenuActive ? 0 : -1}
          />
        );
      })}
    </div>
  );
}
